// useWeather — local weather observation, cached.
//
// Source: Open-Meteo (https://open-meteo.com/). Free, no API key, no
// auth. ECMWF + national-service backed; rate limit 10k/day per IP
// which we'll never approach with the caching below.
//
// Caching strategy (per CLAUDE.md egress discipline applied to
// external APIs too):
//   - localStorage TTL 30 min for "current" conditions. The user
//     doesn't notice if "72°F sunny" is 20 minutes stale.
//   - Geolocation cached separately for 24h — coordinates change
//     much slower than conditions, and re-prompting for permission
//     on every refresh would be a UX disaster.
//   - Failure modes are graceful nulls. A network blip / denied
//     permission / API outage just means callers see weather: null
//     and skip whatever reactor they were about to fire.
//
// Returned shape:
//   {
//     loading,
//     weather: null | {
//       tempF,        number — current air temperature
//       feelsLikeF,   number — apparent temp (wind + humidity)
//       condition,    "clear" | "cloudy" | "rain" | "snow" | "storm"
//                     | "fog" | "drizzle" | "unknown"
//       descriptor,   short copy ("light rain", "partly cloudy")
//       isDaytime,    boolean
//       windMph,      number
//       humidity,     number 0..100
//       observedAt,   ISO string
//       coords,       { lat, lon }   — for debugging / display
//     },
//     refresh,        manual re-fetch (skips cache)
//     error,          last error message or null
//   }
//
// Why not store this in Supabase: weather is per-user, per-location,
// stale-tolerable, and the underlying API is free. A DB table would
// add egress + RLS complexity with zero benefit. Move it to the DB
// only if a "weather you cooked in" history feature actually ships.

import { useCallback, useEffect, useState } from "react";
import { readCache, writeCache } from "./useCachedQuery";

const WEATHER_CACHE_KEY = "weather:v1";
const WEATHER_TTL_MS    = 30 * 60 * 1000;   // 30 min

const COORDS_CACHE_KEY  = "weather:coords:v1";
const COORDS_TTL_MS     = 24 * 60 * 60 * 1000; // 24h

// WMO weather code → coarse bucket. Open-Meteo returns a 0..99 code
// (WMO standard). We collapse to ~7 buckets that are useful for
// downstream "what should the music / ads / greeting do?" decisions.
// Full table: https://open-meteo.com/en/docs (search "WMO Weather
// interpretation codes").
function bucketWMO(code) {
  if (code == null) return { condition: "unknown", descriptor: "" };
  if (code === 0)                 return { condition: "clear",   descriptor: "clear" };
  if (code >= 1 && code <= 3)     return { condition: "cloudy",  descriptor: code === 1 ? "mostly clear" : code === 2 ? "partly cloudy" : "overcast" };
  if (code === 45 || code === 48) return { condition: "fog",     descriptor: "foggy" };
  if (code >= 51 && code <= 57)   return { condition: "drizzle", descriptor: "drizzle" };
  if (code >= 61 && code <= 67)   return { condition: "rain",    descriptor: code <= 63 ? "light rain" : code <= 65 ? "rain" : "freezing rain" };
  if (code >= 71 && code <= 77)   return { condition: "snow",    descriptor: code <= 73 ? "light snow" : "snow" };
  if (code >= 80 && code <= 82)   return { condition: "rain",    descriptor: code === 82 ? "heavy showers" : "showers" };
  if (code >= 85 && code <= 86)   return { condition: "snow",    descriptor: "snow showers" };
  if (code >= 95 && code <= 99)   return { condition: "storm",   descriptor: "thunderstorm" };
  return { condition: "unknown", descriptor: "" };
}

// Resolve user coordinates. Tries the cached pair first, falls back
// to navigator.geolocation. If the user has denied permission, we
// surface the rejection — callers fall through to weather: null.
function getCoords() {
  return new Promise((resolve, reject) => {
    const cached = readCache(COORDS_CACHE_KEY, COORDS_TTL_MS);
    if (cached?.lat != null && cached?.lon != null) {
      resolve(cached);
      return;
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("geolocation unavailable"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const out = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        writeCache(COORDS_CACHE_KEY, out);
        resolve(out);
      },
      (err) => reject(err),
      { enableHighAccuracy: false, maximumAge: 6 * 60 * 60 * 1000, timeout: 10_000 },
    );
  });
}

async function fetchWeather({ lat, lon }) {
  const url = new URL("https://api.open-meteo.com/v1/forecast");
  url.searchParams.set("latitude",  lat);
  url.searchParams.set("longitude", lon);
  url.searchParams.set("current",   "temperature_2m,relative_humidity_2m,apparent_temperature,is_day,weathercode,wind_speed_10m");
  url.searchParams.set("temperature_unit", "fahrenheit");
  url.searchParams.set("wind_speed_unit",  "mph");
  url.searchParams.set("timezone", "auto");
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`weather api ${res.status}`);
  const json = await res.json();
  const c = json.current || {};
  const bucket = bucketWMO(c.weathercode);
  return {
    tempF:       Math.round(c.temperature_2m ?? 0),
    feelsLikeF:  Math.round(c.apparent_temperature ?? c.temperature_2m ?? 0),
    condition:   bucket.condition,
    descriptor:  bucket.descriptor,
    isDaytime:   c.is_day === 1,
    windMph:     Math.round(c.wind_speed_10m ?? 0),
    humidity:    Math.round(c.relative_humidity_2m ?? 0),
    observedAt:  c.time ? new Date(c.time).toISOString() : new Date().toISOString(),
    coords:      { lat, lon },
  };
}

export function useWeather() {
  // Hydrate synchronously from cache so the first render already has
  // weather available — no flash from "no weather" → "weather here"
  // for users who've opened the app recently.
  const initial = readCache(WEATHER_CACHE_KEY, WEATHER_TTL_MS) || null;
  const [weather, setWeather] = useState(initial);
  const [loading, setLoading] = useState(!initial);
  const [error,   setError]   = useState(null);

  const load = useCallback(async (skipCache = false) => {
    if (!skipCache) {
      const cached = readCache(WEATHER_CACHE_KEY, WEATHER_TTL_MS);
      if (cached) {
        setWeather(cached);
        setLoading(false);
        return;
      }
    }
    setLoading(true);
    try {
      const coords = await getCoords();
      const w = await fetchWeather(coords);
      writeCache(WEATHER_CACHE_KEY, w);
      setWeather(w);
      setError(null);
    } catch (e) {
      setError(e?.message || "weather unavailable");
      setWeather(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Re-check on every visibility-flip back to foreground. If the user
  // backgrounded the app for hours, the cache is stale and they'd
  // notice "still 50°F" hanging on into the afternoon. Cheap to
  // re-validate via cache hit; the actual fetch only fires on TTL.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  const refresh = useCallback(() => load(true), [load]);

  return { loading, weather, refresh, error };
}
